import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import PageEditView from '../components/PageEditView';

import DesignForm from '@/dashboard/scenes/PageEdit/scenes/PageDesign/DesignForm';
import { getDesignSections } from '@/dashboard/scenes/PageEdit/scenes/PageDesign/theming/getDesignSections';
import {
  DESIGN_GROUP_ICONS,
  DESIGN_SECTION_ICONS,
} from '@/dashboard/scenes/PageEdit/scenes/PageDesign/theming/designSectionIcons';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@dashboard/components/ui/sidebar';
import {
  Caret,
  NAV_ITEM_CLASS,
  NAV_SUB_ITEM_CLASS,
  NAV_SUB_LIST_CLASS,
} from '@dashboard/layouts/NavigationParts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';
import usePage from '@dashboard/hooks/usePage';
import usePrevious from '@dashboard/hooks/usePrevious';
import storage from '@dashboard/services/storage';
import { get, isEmpty } from 'lodash';
import getPageTypeLabel from '@/dashboard/services/getPageTypeLabel';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';

// Fallback for a caller that has no seller to hand. DesignNavigation passes the
// seller-aware list instead, so feature-flagged sections stay out of the nav.
const DESIGN_SECTIONS = getDesignSections();

// Groups sections into top-level items and heading-keyed groups.
// Sections with `topLevel: true` always render at the top level and break out of
// any open heading group.
function buildDesignNavGroups(sections) {
  const groups = [];
  let current = { kind: 'top', items: [] };
  groups.push(current);
  for (const section of sections) {
    if (section.type === 'heading') {
      current = { kind: 'group', label: section.label, items: [] };
      groups.push(current);
    } else if (section.topLevel) {
      current = { kind: 'top', items: [section] };
      groups.push(current);
    } else {
      current.items.push(section);
    }
  }
  return groups;
}

const DESIGN_NAV_GROUP_STORAGE_KEY = 'design-nav-group-open';
const DESIGN_NAV_GROUP_DEFAULTS = {
  Elements: false,
};

function readDesignNavGroupOpen() {
  try {
    const stored = storage.local.getItem(DESIGN_NAV_GROUP_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) || {};
  } catch {
    return {};
  }
}

function writeDesignNavGroupOpen(next) {
  try {
    storage.local.setItem(DESIGN_NAV_GROUP_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage failures (quota, privacy mode, etc.)
  }
}

function useDesignNavGroupOpen(label) {
  const defaultOpen = DESIGN_NAV_GROUP_DEFAULTS[label] ?? true;
  const [open, setOpen] = useState(() => {
    const stored = readDesignNavGroupOpen();
    return stored[label] ?? defaultOpen;
  });

  const setOpenAndPersist = useCallback(
    nextOpen => {
      setOpen(nextOpen);
      const stored = readDesignNavGroupOpen();
      writeDesignNavGroupOpen({ ...stored, [label]: nextOpen });
    },
    [label],
  );

  return [open, setOpenAndPersist, setOpen];
}

function ChangesDot() {
  return <span className="ml-auto size-1 shrink-0 rounded-full bg-gray-400" />;
}

const ThemeGalleryContext = createContext({ galleryOpen: false, setGalleryOpen: () => {} });
const useThemeGallery = () => useContext(ThemeGalleryContext);

const scrollToSection = sectionId => {
  // If the all-themes gallery is open, close it first. We defer the rest by a
  // tick so React can commit, the form can remount, and its expand listener
  // can re-attach before we dispatch the expand event.
  window.dispatchEvent(new CustomEvent('close-theme-gallery'));

  setTimeout(() => {
    // Expand the section first so it's measurable before scrolling
    window.dispatchEvent(new CustomEvent('expand-design-section', { detail: sectionId }));

    // Wait for the collapsible open animation (150ms) to finish before measuring
    // position, otherwise the scroll target shifts mid-scroll as it expands.
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const elementTop = rect.top + scrollTop;
        const offsetPosition = elementTop - 64;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 160);
  }, 0);
};

export const useDesignNavigation = (isOnDesignTab, sections = DESIGN_SECTIONS) => {
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const lastActiveSection = useRef(sections[0].id);
  const rafId = useRef(null);

  useEffect(() => {
    if (!isOnDesignTab) return;

    const findCurrentSection = () => {
      let bestSection = sections[0].id;
      let bestOverlap = 0;
      const zoneBottom = window.innerHeight / 3;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const overlapTop = Math.max(rect.top, 0);
          const overlapBottom = Math.min(rect.bottom, zoneBottom);
          const overlap = Math.max(0, overlapBottom - overlapTop);
          if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestSection = section.id;
          }
        }
      }

      setActiveSection(bestSection);
      lastActiveSection.current = bestSection;
    };

    findCurrentSection();
    const resetTimeout = setTimeout(findCurrentSection, 100);

    return () => {
      clearTimeout(resetTimeout);
    };
  }, [isOnDesignTab, sections]);

  useEffect(() => {
    if (!isOnDesignTab) return;

    const updateActiveSection = () => {
      let bestSection = null;
      let bestOverlap = 0;
      const zoneBottom = window.innerHeight / 3;

      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const overlapTop = Math.max(rect.top, 0);
          const overlapBottom = Math.min(rect.bottom, zoneBottom);
          const overlap = Math.max(0, overlapBottom - overlapTop);

          if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestSection = section.id;
          }
        }
      });

      if (bestSection && bestSection !== lastActiveSection.current) {
        setActiveSection(bestSection);
        lastActiveSection.current = bestSection;
      }
    };

    const handleScroll = () => {
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        updateActiveSection();
        rafId.current = null;
      });
    };

    const observer = new IntersectionObserver(handleScroll, {
      root: null,
      rootMargin: '-20px 0px -50% 0px',
      threshold: 0.1,
    });

    const setupObserver = () => {
      let observedElements = 0;
      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          observer.observe(element);
          observedElements++;
        }
      });
      return observedElements;
    };

    let observedCount = setupObserver();

    let retryCount = 0;
    const maxRetries = 10;
    const retryInterval = setInterval(() => {
      if (observedCount === 0 && retryCount < maxRetries) {
        observedCount = setupObserver();
        retryCount++;
      } else {
        clearInterval(retryInterval);
      }
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearInterval(retryInterval);
      if (rafId.current) cancelAnimationFrame(rafId.current);
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOnDesignTab, sections]);

  return { activeSection, scrollToSection, sections };
};

/**
 * Check whether any overrideKey path has a non-empty value in themeOverrides.
 * overrideKeys are dot-paths like "variables.tokens" – we look them up inside
 * the overrides object (which already lives under themeOverrides directly).
 */
const sectionHasOverrides = (overrideKeys, overrides) => {
  if (!overrides || isEmpty(overrides)) return false;

  for (const key of overrideKeys) {
    const value = get(overrides, key);
    if (value !== undefined && value !== null && !isEmpty(value)) {
      return true;
    }
  }
  return false;
};

const MemoNavButton = React.memo(function MemoNavButton({
  id,
  label,
  isActive,
  hasChanges,
  onSelect,
}) {
  const handleClick = useCallback(() => onSelect(id), [id, onSelect]);
  const Icon = DESIGN_SECTION_ICONS[id];
  return (
    <SidebarMenuButton isActive={isActive} onClick={handleClick} className={NAV_ITEM_CLASS}>
      {Icon && <Icon />}
      <span>{label}</span>
      {hasChanges && <ChangesDot />}
    </SidebarMenuButton>
  );
});

const MemoNavSubButton = React.memo(function MemoNavSubButton({
  id,
  label,
  isActive,
  hasChanges,
  onSelect,
}) {
  const handleClick = useCallback(() => onSelect(id), [id, onSelect]);
  const Icon = DESIGN_SECTION_ICONS[id];
  return (
    <SidebarMenuSubButton asChild isActive={isActive} className={NAV_SUB_ITEM_CLASS}>
      <button type="button" onClick={handleClick} className="w-full text-left">
        {Icon && <Icon />}
        <span>{label}</span>
        {hasChanges && <ChangesDot />}
      </button>
    </SidebarMenuSubButton>
  );
});

function DesignNavGroup({ label, items, activeSection, sectionsWithChanges, onSelect }) {
  const [isOpen, setOpenAndPersist, setOpenTransient] = useDesignNavGroupOpen(label);
  const GroupIcon = DESIGN_GROUP_ICONS[label];

  const containsActive = items.some(s => s.id === activeSection);
  const wasContainsActive = usePrevious(containsActive);

  useEffect(() => {
    if (containsActive && !wasContainsActive && !isOpen) {
      setOpenTransient(true);
    }
  }, [containsActive, wasContainsActive, isOpen, setOpenTransient]);

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setOpenAndPersist}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton aria-expanded={isOpen} className={NAV_ITEM_CLASS}>
            {GroupIcon && <GroupIcon />}
            <span>{label}</span>
            <Caret isOpen={isOpen} />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className={NAV_SUB_LIST_CLASS}>
            {items.map(section => (
              <SidebarMenuSubItem key={section.id}>
                <MemoNavSubButton
                  id={section.id}
                  label={section.label}
                  isActive={activeSection === section.id}
                  hasChanges={sectionsWithChanges.has(section.id)}
                  onSelect={onSelect}
                />
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export const DesignNavigation = React.memo(function DesignNavigation() {
  const location = useLocation();
  const history = useHistory();
  const isOnDesignTab = location.pathname.includes('/design/theme');
  const { seller } = useAuth();
  const sections = useMemo(() => getDesignSections({ seller }), [seller]);
  const { activeSection: detectedActiveSection } = useDesignNavigation(isOnDesignTab, sections);
  const { galleryOpen } = useThemeGallery();
  // While the all-themes gallery overlay is open the section anchors aren't in
  // the DOM, so the IntersectionObserver-driven activeSection is stale. Pin it
  // to 'theme' so the nav reflects what the user is looking at.
  // The last sections can never reach the scroll-spy's zone, so a clicked section
  // stays marked until the user scrolls or types themselves.
  const [clickedSection, setClickedSection] = useState(null);
  useEffect(() => {
    if (!clickedSection) return undefined;
    const release = () => setClickedSection(null);
    window.addEventListener('wheel', release, { passive: true });
    window.addEventListener('touchmove', release, { passive: true });
    window.addEventListener('keydown', release);
    return () => {
      window.removeEventListener('wheel', release);
      window.removeEventListener('touchmove', release);
      window.removeEventListener('keydown', release);
    };
  }, [clickedSection]);

  const activeSection = !isOnDesignTab
    ? null
    : galleryOpen
      ? 'theme'
      : (clickedSection ?? detectedActiveSection);
  const { page } = usePage();

  // Off the Theme screen the anchors are not in the page, so open Theme at the section.
  const onSelect = useCallback(
    id => {
      if (!isOnDesignTab) {
        history.push(`/pages/${page._id}/customize/design/theme#${id}`);
        return;
      }
      setClickedSection(id);
      scrollToSection(id);
    },
    [isOnDesignTab, history, page._id],
  );

  const navGroups = useMemo(() => buildDesignNavGroups(sections), [sections]);

  const overrides = page?.themeOverrides;

  const sectionsWithChanges = useMemo(() => {
    const set = new Set();
    if (!overrides || isEmpty(overrides?.variables)) return set;

    for (const section of sections) {
      if (section.overrideKeys && sectionHasOverrides(section.overrideKeys, overrides)) {
        set.add(section.id);
      }
    }

    return set;
  }, [overrides, sections]);

  return (
    <>
      {navGroups.map(group => {
        if (group.kind === 'top') {
          return group.items.map(section => (
            <SidebarMenuItem key={section.id}>
              <MemoNavButton
                id={section.id}
                label={section.label}
                isActive={activeSection === section.id}
                hasChanges={sectionsWithChanges.has(section.id)}
                onSelect={onSelect}
              />
            </SidebarMenuItem>
          ));
        }

        return (
          <DesignNavGroup
            key={`group-${group.label}`}
            label={group.label}
            items={group.items}
            activeSection={activeSection}
            sectionsWithChanges={sectionsWithChanges}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
});

const PageDesign = () => {
  const location = useLocation();
  const { page } = usePage();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const preGalleryScrollRef = useRef(null);

  const isOnDesignTab = location.pathname.includes('/design/theme');

  // Capture form scroll before the gallery mounts so we can restore it on
  // close. Setting state first would let the layout shift before we read.
  const setGalleryOpenWithScrollMemory = useCallback(nextOpen => {
    if (nextOpen) {
      preGalleryScrollRef.current = window.scrollY;
      setGalleryOpen(true);
      window.scrollTo(0, 0);
    } else {
      setGalleryOpen(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (!galleryOpen && preGalleryScrollRef.current != null) {
      const targetY = preGalleryScrollRef.current;
      preGalleryScrollRef.current = null;
      window.scrollTo(0, targetY);
    }
  }, [galleryOpen]);

  useEffect(() => {
    const handler = () => {
      // Programmatic close (nav click) will scroll to a specific section, so
      // skip restoring the pre-gallery position.
      preGalleryScrollRef.current = null;
      setGalleryOpen(false);
    };
    window.addEventListener('close-theme-gallery', handler);
    return () => window.removeEventListener('close-theme-gallery', handler);
  }, []);

  // Deep-link support: a URL hash like `#prices` on the Design tab scrolls to
  // and opens that section. The target lives inside the (async) DesignForm, so
  // poll for the anchor before handing off to scrollToSection (which expands the
  // section then scrolls). Re-runs when the hash changes (e.g. a #prices link).
  useEffect(() => {
    if (!isOnDesignTab) return undefined;
    const sectionId = decodeURIComponent(location.hash.replace(/^#/, ''));
    console.log(sectionId);
    if (!sectionId) return undefined;

    let timer;
    let attempts = 0;

    const tryScroll = () => {
      console.log(document.getElementById(sectionId));
      if (document.getElementById(sectionId)) {
        scrollToSection(sectionId);
      } else if (attempts++ < 20) {
        timer = setTimeout(tryScroll, 100);
      }
      console.log('trying');
    };
    tryScroll();
    return () => clearTimeout(timer);
  }, [isOnDesignTab, location.hash]);

  const galleryContextValue = useMemo(
    () => ({ galleryOpen, setGalleryOpen: setGalleryOpenWithScrollMemory }),
    [galleryOpen, setGalleryOpenWithScrollMemory],
  );

  return (
    <ThemeGalleryContext.Provider value={galleryContextValue}>
      <PageEditView
        isOnDesignTab={isOnDesignTab}
        tabs={[
          {
            tab: 'design',
            path: '/theme',
            label: 'Theme',
            description: `Customize your ${getPageTypeLabel(page)} and create reusable themes.`,
            component: (
              <DesignForm
                galleryOpen={galleryOpen}
                setGalleryOpen={setGalleryOpenWithScrollMemory}
              />
            ),
            hidden: true,
            hideHeader: galleryOpen,
          },
        ]}
      />
    </ThemeGalleryContext.Provider>
  );
};

export default PageDesign;
